import { createTheme } from '@mui/material/styles';

// Create MUI theme with complete color palette
export const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5E00FF',    // Primary purple
      light: '#F4F4F7',   // Light gray-purple
      dark: '#4700C1',    // Dark purple
      contrastText: '#fff',
    },
    secondary: {
      main: '#EE29B3',    // Pink
      light: '#FAEDF6',   // Light pink
      dark: '#A80075',    // Dark pink
      contrastText: '#fff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: 'hsl(240, 10%, 3.9%)',
      secondary: 'hsl(240, 3.8%, 42%)',
    },
    error: {
      main: 'hsl(0, 84.2%, 60.2%)',
      contrastText: '#ffffff',
    },
    success: {
      main: 'hsl(142, 76%, 36%)',
      contrastText: '#ffffff',
    },
    divider: 'hsl(240, 5.9%, 90%)',
  },
  typography: {
    fontFamily: [
      'Public Sans',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif'
    ].join(','),
    body1: {
      fontSize: '1.125rem', // 18px
      color: 'hsl(240, 3.8%, 42%)', // text.secondary
    },
    body2: {
      fontSize: '1.125rem', // 18px
      color: 'hsl(240, 3.8%, 42%)', // text.secondary
    },
    h1: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 700,
      fontSize: '1.5rem', // 24px
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 700,
      fontSize: '1.375rem', // 22px
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 600,
      fontSize: '1.125rem', // 18px
    },
    h4: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 400,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 400,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 400,
      letterSpacing: '-0.01em',
    },
  },
  shape: {
    borderRadius: 4, 
  },
  components: {
    // CSS Baseline override for body styles
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          margin: 0,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
        '@keyframes spin': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
      }),
    },
    
    // Accordion styling to ensure proper spacing and subtitle hiding
    MuiAccordion: {
      styleOverrides: {
        root: {
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
          '&.Mui-expanded .MuiTypography-body2': {
            display: 'none',
          },
        },
      },
    },
    
    // FormControlLabel styling to eliminate repeated sx
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          width: '100%',
          marginBottom: 0,
          padding: '8px 8px',
          borderRadius: 8,
          border: `1px solid transparent`,
          transition: 'all 0.2s',
          '&.Mui-disabled': {
            // Make all text including nested Typography use disabled color
            '& .MuiTypography-root': {
              color: 'rgba(0, 0, 0, 0.38)',
            },
          },
        },
        label: {
          color: 'hsl(240, 10%, 3.9%)', // text.primary - darker color
        },
      },
    },
    
    // RadioGroup styling for checked label weight
    MuiRadioGroup: {
      styleOverrides: {
        root: {
          '& .MuiRadio-root.Mui-checked + .MuiFormControlLabel-label': {
            fontWeight: 600,
          },
        },
      },
    },
    
    // FormGroup styling for checked label weight  
    MuiFormGroup: {
      styleOverrides: {
        root: {
          '& .MuiCheckbox-root.Mui-checked + .MuiFormControlLabel-label': {
            fontWeight: 600,
          },
        },
      },
    },
    
    // Button styling for text variant and tap targets
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: '44px', // Ensure adequate tap target size
        },
      },
    },
    
    // FormLabel styling
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: '1.125rem',
          marginBottom: '16px',
          color: 'hsl(240, 10%, 3.9%)', // text.primary
          '&.Mui-focused': {
            color: 'hsl(240, 10%, 3.9%)', // Keep text.primary when focused
          },
          '&.Mui-filled': {
            color: 'hsl(240, 10%, 3.9%)', // Keep text.primary when filled
          },
        },
      },
    },
    
    // LinearProgress customization
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#F4F4F7', // primary.light equivalent
        },
      },
    },
    
    // FormHelperText styling for hint/help text
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          color: 'hsl(240, 3.8%, 42%)', // text.secondary
        },
      },
    },
    
    // TextField helper text styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiFormHelperText-root': {
            fontSize: '1.125rem',
            color: 'hsl(240, 3.8%, 42%)', // text.secondary
          },
        },
      },
    },
    
    // InputLabel styling for text fields
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});